import { getBaseUrl } from "@Front/Api/helpers/configHelpers";
import { LoggerService } from "@Front/Services/LoggerService/LoggerService";

/**
 * Appends a path under the configured API root (includes `/v1`, no trailing slash on base).
 * @param relativePath segment without leading slash, e.g. `public/site-config`
 */
function urlUnderApiRoot(relativePath: string): string {
	const base = getBaseUrl();
	const path = relativePath.startsWith("/") ? relativePath.slice(1) : relativePath;
	return `${base}/${path}`;
}

/** Base URL for public third-party auth routes (`.../public/third-party`). */
export function getThirdPartyPublicApiBaseUrl(): string {
	return urlUnderApiRoot("public/third-party");
}

/** POST target for email login candidates snapshot. */
export function getPublicEmailAuthCandidatesUrl(): string {
	return urlUnderApiRoot("public/email-auth/candidates");
}

/** GET `public/config` (runtime + FrontendConfigLoader). */
export function getPublicConfigUrl(): string {
	return urlUnderApiRoot("public/config");
}

/** GET `public/site-config` (theme CSS variables, product code, legal URLs). */
export function getPublicSiteConfigUrl(): string {
	return urlUnderApiRoot("public/site-config");
}

/** POST multipart `public/verify-file` (document public verification). */
export function getPublicVerifyFileUrl(): string {
	return urlUnderApiRoot("public/verify-file");
}

/** GET branding SVG — même base que {@link getPublicSiteConfigUrl} (évite `<img src="/api/...">` vers l’hôte front si l’API est ailleurs). */
export function getPublicBrandingLogoMainUrl(): string {
	return urlUnderApiRoot("public/branding/logo-main.svg");
}

export function getPublicBrandingLogoSmallUrl(): string {
	return urlUnderApiRoot("public/branding/logo-small.svg");
}

export function getPublicBrandingFaviconUrl(): string {
	return urlUnderApiRoot("public/branding/favicon.svg");
}

/**
 * Résout une URL renvoyée par `site-config` : chemins absolus `/api/...` ou relatifs `public/...` par rapport à {@link getBaseUrl}.
 * Les URLs `http(s)://` complètes ne sont conservées que si **même origine** que l’API (évite logos / favicons d’une autre ligne ou domaine tiers sur le FQDN courant).
 */
export function resolvePublicBrandingMediaUrl(href: string | null | undefined, fallback: string): string {
	const t = typeof href === "string" ? href.trim() : "";
	if (!t) {
		return fallback;
	}
	if (t.startsWith("https://") || t.startsWith("http://")) {
		try {
			const abs = new URL(t);
			const api = new URL(`${getBaseUrl()}/`);
			if (abs.origin !== api.origin) {
				LoggerService.warn("[resolvePublicBrandingMediaUrl] Absolute branding URL is not same-origin as API — using fallback", {
					href: t,
					apiOrigin: api.origin,
				});
				return fallback;
			}
			return t;
		} catch {
			return fallback;
		}
	}
	try {
		return new URL(t, `${getBaseUrl()}/`).href;
	} catch {
		return fallback;
	}
}
